/**
 * Size Chart Enhanced - JavaScript Functionality
 * Handles tab switching and unit toggle (inches/cm)
 */

class SizeChartEnhanced {
  constructor() {
    this.init();
  }

  init() {
    this.setupTabSwitching();
    this.setupUnitToggle();
    this.setupAccessibility();
  }

  /**
   * Setup tab switching functionality
   */
  setupTabSwitching() {
    const tabs = document.querySelectorAll('.size-chart-tab');
    const contents = document.querySelectorAll('.size-chart-content');

    if (tabs.length === 0) return;

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();

        // Remove active class from all tabs and contents
        tabs.forEach(t => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        contents.forEach(c => c.classList.remove('active'));

        // Add active class to clicked tab
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');

        // Show corresponding content
        const targetId = tab.getAttribute('data-tab');
        const targetContent = document.getElementById(targetId);

        if (targetContent) {
          targetContent.classList.add('active');

          // Toggle unit toggle visibility based on tab setting
          this.toggleUnitToggleVisibility(targetContent);

          // Smooth scroll to content on mobile
          if (window.innerWidth < 750) {
            setTimeout(() => {
              targetContent.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
              });
            }, 100);
          }
        }

        // Save preference
        this.saveTabPreference(index);
      });

      // Keyboard navigation
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          tab.click();
        }

        // Arrow key navigation
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const direction = e.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = (index + direction + tabs.length) % tabs.length;
          tabs[nextIndex].focus();
          tabs[nextIndex].click();
        }
      });
    });

    // Restore last selected tab
    this.restoreTabPreference(tabs);

    // Set initial unit toggle visibility based on first active tab
    const activeContent = document.querySelector('.size-chart-content.active');
    if (activeContent) {
      this.toggleUnitToggleVisibility(activeContent);
    }
  }

  /**
   * Toggle unit toggle visibility based on tab setting
   */
  toggleUnitToggleVisibility(tabContent) {
    const unitToggleWrapper = document.querySelector('.unit-toggle-wrapper');
    if (!unitToggleWrapper) return;

    const hideUnitToggle = tabContent.getAttribute('data-hide-unit-toggle') === 'true';

    if (hideUnitToggle) {
      unitToggleWrapper.style.display = 'none';
      // When unit toggle is hidden, show whatever table exists in this tab
      this.showAvailableTable(tabContent);
    } else {
      unitToggleWrapper.style.display = 'flex';
      // Apply the current unit selection to this tab
      this.applyCurrentUnitToTab(tabContent);
    }
  }

  /**
   * Show whatever table is available in a tab (for tabs with only one table type)
   */
  showAvailableTable(tabContent) {
    const tables = tabContent.querySelectorAll('.unit-table');
    tables.forEach(table => {
      table.style.display = 'block';
      table.setAttribute('aria-hidden', 'false');
    });
  }

  /**
   * Apply the current unit selection to a specific tab
   */
  applyCurrentUnitToTab(tabContent) {
    const activeUnitBtn = document.querySelector('.unit-btn.active');
    if (!activeUnitBtn) return;

    const selectedUnit = activeUnitBtn.getAttribute('data-unit');
    const tables = tabContent.querySelectorAll('.unit-table');

    let hasSelectedUnit = false;
    tables.forEach(table => {
      if (table.getAttribute('data-unit') === selectedUnit) {
        hasSelectedUnit = true;
      }
    });

    // If the tab doesn't have the selected unit's table, show whatever is available
    if (!hasSelectedUnit) {
      tables.forEach(table => {
        table.style.display = 'block';
        table.setAttribute('aria-hidden', 'false');
      });
    } else {
      tables.forEach(table => {
        const tableUnit = table.getAttribute('data-unit');
        if (tableUnit === selectedUnit) {
          table.style.display = 'block';
          table.setAttribute('aria-hidden', 'false');
        } else {
          table.style.display = 'none';
          table.setAttribute('aria-hidden', 'true');
        }
      });
    }
  }

  /**
   * Setup unit toggle functionality (inches/cm)
   */
  setupUnitToggle() {
    const unitBtns = document.querySelectorAll('.unit-btn');
    const unitTables = document.querySelectorAll('.unit-table');

    if (unitBtns.length === 0) return;

    unitBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        // Get selected unit
        const selectedUnit = btn.getAttribute('data-unit');

        // Update button states
        unitBtns.forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');

        // Toggle table visibility
        unitTables.forEach(table => {
          const tableUnit = table.getAttribute('data-unit');
          if (tableUnit === selectedUnit) {
            table.style.display = 'block';
            table.setAttribute('aria-hidden', 'false');
          } else {
            table.style.display = 'none';
            table.setAttribute('aria-hidden', 'true');
          }
        });

        // Save preference
        this.saveUnitPreference(selectedUnit);
      });

      // Keyboard support
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });
    });

    // Restore last selected unit
    this.restoreUnitPreference(unitBtns);
  }

  /**
   * Setup accessibility features
   */
  setupAccessibility() {
    // Add ARIA attributes to tabs
    const tabs = document.querySelectorAll('.size-chart-tab');
    tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', tab.classList.contains('active') ? 'true' : 'false');
      tab.setAttribute('tabindex', tab.classList.contains('active') ? '0' : '-1');
    });

    // Add ARIA attributes to tab container
    const tabContainer = document.querySelector('.size-chart-tabs');
    if (tabContainer) {
      tabContainer.setAttribute('role', 'tablist');
    }

    // Add ARIA attributes to content panels
    const contents = document.querySelectorAll('.size-chart-content');
    contents.forEach(content => {
      content.setAttribute('role', 'tabpanel');
      content.setAttribute('aria-hidden', content.classList.contains('active') ? 'false' : 'true');
    });

    // Add ARIA attributes to unit toggle buttons
    const unitBtns = document.querySelectorAll('.unit-btn');
    unitBtns.forEach(btn => {
      btn.setAttribute('role', 'button');
      btn.setAttribute('aria-pressed', btn.classList.contains('active') ? 'true' : 'false');
    });

    // Add table captions for screen readers
    const tables = document.querySelectorAll('.size-chart-table table');
    tables.forEach((table, index) => {
      if (!table.querySelector('caption')) {
        const caption = document.createElement('caption');
        caption.textContent = 'Size Chart Table';
        caption.style.position = 'absolute';
        caption.style.width = '1px';
        caption.style.height = '1px';
        caption.style.overflow = 'hidden';
        caption.style.clip = 'rect(0,0,0,0)';
        table.insertBefore(caption, table.firstChild);
      }
    });
  }

  /**
   * Save tab preference to localStorage
   */
  saveTabPreference(tabIndex) {
    try {
      localStorage.setItem('sizeChartTabPreference', tabIndex);
    } catch (e) {
      // localStorage might be disabled
      console.warn('Could not save tab preference:', e);
    }
  }

  /**
   * Restore tab preference from localStorage
   */
  restoreTabPreference(tabs) {
    try {
      const savedIndex = localStorage.getItem('sizeChartTabPreference');
      if (savedIndex !== null && tabs[savedIndex]) {
        // Don't auto-switch on first visit, only restore if previously set
        const hasVisited = localStorage.getItem('sizeChartVisited');
        if (hasVisited) {
          tabs[savedIndex].click();
        }
        localStorage.setItem('sizeChartVisited', 'true');
      }
    } catch (e) {
      // localStorage might be disabled
      console.warn('Could not restore tab preference:', e);
    }
  }

  /**
   * Save unit preference to localStorage
   */
  saveUnitPreference(unit) {
    try {
      localStorage.setItem('sizeChartUnitPreference', unit);
    } catch (e) {
      // localStorage might be disabled
      console.warn('Could not save unit preference:', e);
    }
  }

  /**
   * Restore unit preference from localStorage
   */
  restoreUnitPreference(unitBtns) {
    try {
      const savedUnit = localStorage.getItem('sizeChartUnitPreference');
      if (savedUnit) {
        const targetBtn = Array.from(unitBtns).find(
          btn => btn.getAttribute('data-unit') === savedUnit
        );
        if (targetBtn && !targetBtn.classList.contains('active')) {
          targetBtn.click();
        }
      }
    } catch (e) {
      // localStorage might be disabled
      console.warn('Could not restore unit preference:', e);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SizeChartEnhanced();
  });
} else {
  new SizeChartEnhanced();
}

// Re-initialize on Shopify section load (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new SizeChartEnhanced();
  });
}
